import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { buildDxaUrl, mergeDxaParams, validateDxaConfig } from '../utils/dxaUrlBuilder';
import AgentView from './AgentView';
import PegaWebEmbed from './PegaWebEmbed';

// Helper function to determine if a color is light or dark
const isLightColor = (color) => {
    if (!color) return false;

    // Convert hex to RGB
    let r, g, b;
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
    } else if (color.startsWith('rgb')) {
        const rgb = color.match(/\d+/g);
        r = parseInt(rgb[0]);
        g = parseInt(rgb[1]);
        b = parseInt(rgb[2]);
    } else {
        return false;
    }

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
};

export default function AppView() {
    const url = useSelector((state) => state.channel.appUrl);
    const appConfig = useSelector((state) => state.channel.channelConfig.app);
    const appPegaWebEmbedPropsRaw = useSelector((state) => state.channel.appPegaWebEmbedProps);
    const webEmbedConfig = useSelector((state) => state.channel.webEmbedConfig); // SidePanel config (DO NOT use for App channel credentials!)
    const webEmbedBranding = useSelector((state) => state.channel.webEmbedBranding); // Brand theme
    const hostServer = useSelector((state) => state.channel.hostServer);
    const applicationAlias = useSelector((state) => state.channel.applicationAlias);
    const debug = useSelector((state) => state.channel.debug); // Debug flag from config

    // Brand switching support
    const currentBrand = useSelector((state) => state.channel.currentBrand);

    // Agent configuration for orb
    const agentConfig = useSelector((state) => state.channel.channelConfig.agent);
    const showAgentOrb = appConfig?.showAgentOrb !== false; // Default to true if not specified

    // DXA support
    const dxaConfig = useSelector((state) => state.channel.dxaConfig);
    const dxaProps = useSelector((state) => state.channel.dxaProps);
    const pegaEnabled = useSelector((state) => state.channel.pegaEnabled);

    // Watch for brand changes by tracking the brand ID
    const currentBrandId = currentBrand?.id;

    const [isLoading, setIsLoading] = useState(true);
    const [dxaUrl, setDxaUrl] = useState(undefined);
    const [isAgentOpen, setIsAgentOpen] = useState(false);
    const [currentIframeUrl, setCurrentIframeUrl] = useState('');
    const [iframeKey, setIframeKey] = useState(0);
    const location = useLocation();

    const containerRef = useRef(null); // Ref to the AppView container

    // Memoize appPegaWebEmbedProps to stabilize it for useMemo dependencies
    const appPegaWebEmbedProps = useMemo(() => appPegaWebEmbedPropsRaw || {}, [appPegaWebEmbedPropsRaw]);

    // Reconstruct URL whenever brand or config changes
    useEffect(() => {
        console.log('[AppView] 🔄 Effect running - Brand/Config changed', {
            currentBrandId,
            brandName: currentBrand?.name,
            dxaBaseUrl: currentBrand?.dxa?.baseUrl,
            hostServer,
            currentIframeUrl,
            explicitUrl: url,
            appConfig: {
                hasConfig: !!appConfig,
                action: appConfig?.action,
                pageID: appConfig?.pageID,
                pageClass: appConfig?.pageClass,
                hasWebEmbed: !!appConfig?.webEmbed
            }
        });


        // CASE 0: URL coming from Demo Application
        if (url && url.startsWith('data:text/html')) {
            console.log('[AppView] 📧 URL from demo provided (containing html in url), using as-is');
            setCurrentIframeUrl(url);
            setIframeKey(prev => prev + 1); // Force iframe reload
            return;
        }

        // CASE 0.5: Static iframe URL from channel config (channelConfig.app.url).
        // Lets the App channel point at a plain external app instead of a dynamic
        // DXA/Pega URL. A dynamic `url` (from email/SMS/DXA) still takes priority.
        if (!url && appConfig?.url) {
            if (appConfig.url !== currentIframeUrl) {
                console.log('[AppView] 🔗 Using static channelConfig.app.url:', appConfig.url);
                setCurrentIframeUrl(appConfig.url);
                setIframeKey(prev => prev + 1);
            }
            return;
        }

        // CASE 1: Explicit URL set from email/SMS (full DXA URL with credentials)
        if (url !== currentIframeUrl) {
            console.log('[AppView] 📧 Explicit DXA URL provided (from email/SMS), using as-is:', url);
            setCurrentIframeUrl(url);
            setIframeKey(prev => prev + 1); // Force iframe reload
            return;
        }

        // CASE 2: No explicit URL or incomplete URL - construct from brand DXA config
        // Get the baseURL from brand's DXA config (required for App channel)
        const baseURL = currentBrand?.dxa?.baseUrl;

        if (!baseURL) {
            console.warn('[AppView] ⚠️ No DXA baseUrl found in brand config. Brand switching may not work correctly.');
            // Fallback: if no brand DXA config, we can't construct the URL
            return;
        }

        let reconstructedUrl = '';

        try {
            // When brand changes, compare the full baseURL (including path) not just origin
            let brandHasChanged = false;
            if (currentIframeUrl) {
                try {
                    const parsedCurrentUrl = new URL(currentIframeUrl);
                    const parsedBaseUrl = new URL(baseURL);

                    // Compare the base path (everything before query params)
                    // For GitHub Pages: compare the full origin + pathname base
                    const currentBase = parsedCurrentUrl.origin + parsedCurrentUrl.pathname.split('/').slice(0, -1).join('/');
                    const newBase = parsedBaseUrl.origin + parsedBaseUrl.pathname;

                    brandHasChanged = currentBase !== newBase;

                    console.log('[AppView] Brand change detection:', {
                        currentBase,
                        newBase,
                        brandHasChanged
                    });
                } catch (e) {
                    console.warn('[AppView] Could not detect brand change:', e);
                }
            }

            // Start with baseURL path, or default to root
            const parsedBaseUrl = new URL(baseURL);
            let finalPath = parsedBaseUrl.pathname || '/';
            let currentParams = {};

            // Only preserve current path/params if brand hasn't changed
            if (currentIframeUrl && !brandHasChanged) {
                try {
                    const parsedCurrentUrl = new URL(currentIframeUrl);
                    finalPath = parsedCurrentUrl.pathname;
                    currentParams = {};
                    parsedCurrentUrl.searchParams.forEach((value, key) => {
                        currentParams[key] = value;
                    });
                    if (debug) {
                        console.log('[AppView] Preserving current path (brand unchanged):', { finalPath, currentParams });
                    }
                } catch (e) {
                    if (debug) {
                        console.warn('[AppView] Could not parse current iframe URL:', e);
                    }
                }
            } else if (brandHasChanged || !currentIframeUrl) {
                console.log('[AppView] Brand changed or first load - using base path:', {
                    basePath: finalPath,
                    baseURL
                });
            }

            // Construct new URL: baseURL + path + params
            const newUrl = new URL(finalPath, baseURL);

            // First, add all params from current URL (if any)
            Object.entries(currentParams).forEach(([key, value]) => {
                newUrl.searchParams.set(key, value);
            });

            // 4. Navigation params - use defaults from config only if not already present
            // Try multiple config locations for backward compatibility:
            // - channelConfig.app (new structure)
            // - channelConfig.app.webEmbed (nested structure)
            const appAction = appConfig?.action || appConfig?.webEmbed?.action;
            const appPageID = appConfig?.pageID || appConfig?.webEmbed?.pageID;
            const appPageClass = appConfig?.pageClass || appConfig?.webEmbed?.pageClass;
            newUrl.searchParams.set('pegaServerUrl', process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL)
            if (!currentParams.action && appAction) {
                newUrl.searchParams.set('action', appAction);
            }
            if (!currentParams.pageId && appPageID) {
                newUrl.searchParams.set('pageId', appPageID);
            }
            if (!currentParams.pageClass && appPageClass) {
                newUrl.searchParams.set('pageClass', appPageClass);
            }

            reconstructedUrl = newUrl.toString();

            // Always log for debugging brand switching
            console.log('[AppView] Reconstructing URL:', {
                baseURL,
                dxaBaseUrl: currentBrand?.dxa?.baseUrl,
                hostServer,
                finalPath,
                currentParams,
                reconstructedUrl,
                brandId: currentBrand?.id,
                brandName: currentBrand?.name,
                oldUrl: currentIframeUrl,
                urlChanged: reconstructedUrl !== currentIframeUrl
            });
        } catch (e) {
            console.error('[AppView] Error reconstructing URL:', e);
            // If reconstruction fails, keep current URL or use empty string
            reconstructedUrl = currentIframeUrl || '';
        }

        // Always update URL and force reload when brand/config changes
        if (reconstructedUrl && reconstructedUrl !== currentIframeUrl) {
            if (debug) {
                console.log('[AppView] Updating iframe URL from', currentIframeUrl, 'to', reconstructedUrl);
            }
            setCurrentIframeUrl(reconstructedUrl);
            setIframeKey(prev => prev + 1); // Force iframe reload
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBrandId, dxaConfig, hostServer, appConfig, url, debug]); // Removed currentIframeUrl from deps

    // Always show splash screen on mount and when URL changes
    useEffect(() => {
        setIsLoading(true); // Always reset loading on mount
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Determine if we should render PegaWebEmbed or iframe.
    // When Pega is disabled for this config (`"pega": false`), never take the
    // Pega/DXA branches — force the plain iframe from channelConfig.app.url,
    // even if a brand supplies a dxa.baseUrl or webEmbed theme.
    const isPegaWebEmbed = pegaEnabled && appPegaWebEmbedProps && appPegaWebEmbedProps.type === 'pegawebembed';
    const isDxa = pegaEnabled && dxaConfig && validateDxaConfig(dxaConfig);

    // Build DXA URL if needed — uses a short-lived opaque ticket instead of the
    // Cognito JWT so the token is never exposed in browser history or server logs.
    useEffect(() => {
        if (!isDxa) return;
        (async () => {
            const mergedParams = mergeDxaParams(dxaProps || {}, dxaConfig);
            const appId = localStorage.getItem('selectedConfigId');

            let pegaToken = null;
            try {
                const params = new URLSearchParams({ applicationId: appId, channel: 'customer' });
                const resp = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/dxa-ticket?${params}`,
                    {
                        method: 'POST',
                        headers: authService.getAuthHeader(),
                    }
                );
                if (!resp.ok) throw new Error(`dxa-ticket ${resp.status}`);
                const data = await resp.json();
                pegaToken = data.ticket;
            } catch (err) {
                console.error('[AppView] Failed to obtain DXA ticket:', err);
                return;
            }

            setDxaUrl(buildDxaUrl({
                baseUrl: dxaConfig.baseUrl || dxaConfig.pegaServerUrl,
                hostServer: dxaConfig.pegaServerUrl || hostServer,
                followId: authService.getSub(),
                pegaToken,
                channel: 'customer',
                applicationId: appId,
                ...mergedParams
            }));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDxa, dxaProps, dxaConfig, hostServer, applicationAlias, appConfig]);

    // Memoize the enhanced props to prevent unnecessary re-renders
    const enhancedPegaWebEmbedProps = useMemo(() => {
        if (!isPegaWebEmbed) return {};

        // Determine theme settings: theme object takes priority over themeID
        const hasThemeObject = webEmbedBranding?.theme || appConfig?.theme || appConfig?.webEmbed?.theme || webEmbedConfig?.theme;

        const props = {
            // Start with WebSocket props (navigation: pageClass, pageID, action)
            ...appPegaWebEmbedProps,
            // Add infrastructure values (credentials, URLs)
            // Priority: 1. WebSocket props, 2. App channel config (channelConfig.app), 3. App webEmbed nested config, 4. Hardcoded defaults
            // IMPORTANT: DO NOT use webEmbedConfig (sidePanel) for App channel credentials!
            pegaServerUrl: process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL,
            // IMPORTANT: Wrap in iframe container to isolate from SidePanel's PegaWebEmbed
            // Per Pega docs: Multiple web embeds on same page require iframe isolation for all but one
            container: 'iframe',
            // Theme: If theme object exists (brand or config), use it and omit themeID
            // Otherwise, use themeID (brand or config or default)
            ...(hasThemeObject
                ? { theme: webEmbedBranding?.theme || appConfig?.theme || appConfig?.webEmbed?.theme || webEmbedConfig?.theme }
                : { themeID: webEmbedBranding?.themeID || appConfig?.themeID || appConfig?.webEmbed?.themeID || webEmbedConfig?.themeID || "pzAries" }
            ),
        };

        // Remove the 'type' field as it's only for routing logic
        delete props.type;

        return props;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPegaWebEmbed, appPegaWebEmbedProps, appConfig, webEmbedConfig, webEmbedBranding]);

    // Only log in debug mode
    if (debug) {
        if (isPegaWebEmbed) {
            console.log('🔧 AppView enhanced props:', {
                original: appPegaWebEmbedProps,
                enhanced: enhancedPegaWebEmbedProps,
                hostServer,
                hasAppConfig: !!appConfig,
                hasSidePanelWebEmbedConfig: !!webEmbedConfig,
                hasWebEmbedBranding: !!webEmbedBranding,
                hasTheme: !!enhancedPegaWebEmbedProps.theme,
                hasThemeID: !!enhancedPegaWebEmbedProps.themeID,
                theme: enhancedPegaWebEmbedProps.theme,
                themeID: enhancedPegaWebEmbedProps.themeID,
                container: enhancedPegaWebEmbedProps.container,
                themeSource: enhancedPegaWebEmbedProps.theme ? (webEmbedBranding?.theme ? 'Brand' : appConfig?.theme ? 'App Config' : 'App WebEmbed or SidePanel Config') : 'Default themeID'
            });
        }
        if (isDxa) {
            console.log('🌐 AppView DXA URL:', {
                url: dxaUrl,
                action: dxaProps?.action,
                caseId: dxaProps?.caseId,
                assignmentId: dxaProps?.assignmentId,
                baseUrl: dxaConfig?.baseUrl
            });
        }
    }

    return (
        <div ref={containerRef} className="w-full h-full overflow-auto relative" style={{ backgroundColor: '#fff' }}>
            {/* Global CSS to hide scrollbars in iframes */}
            <style>{`
                /* Hide scrollbars globally for better UX */
                iframe[title="App View"],
                iframe[title="DXA View"] {
                    border: none !important;
                }
                
                /* Attempt to hide scrollbars if iframe is same-origin */
                iframe[title="App View"]::-webkit-scrollbar,
                iframe[title="DXA View"]::-webkit-scrollbar {
                    display: none;
                    width: 0;
                    height: 0;
                }
            `}</style>

            {isDxa && dxaUrl ? (
                /* DXA iframe */
                <>
                    {debug && console.log('[AppView] Rendering DXA iframe with src:', dxaUrl)}
                    <iframe
                        src={dxaUrl}
                        title="DXA View"
                        className="w-full h-full"
                        style={{
                            pointerEvents: isLoading ? 'none' : 'auto',
                            backgroundColor: '#fff',
                            border: 'none',
                            margin: 0,
                            padding: 0,
                            display: 'block',
                        }}
                    />
                    <style>{`
                        /* Hide scrollbar for DXA iframe in Chrome, Safari and Opera */
                        iframe[title="DXA View"] {
                            border: none !important;
                        }
                    `}</style>
                </>
            ) : isPegaWebEmbed ? (
                <div
                    className="w-full h-full overflow-auto"
                    style={{
                        backgroundColor: '#fff',
                        // Hide scrollbar for WebKit browsers (Chrome, Safari, Edge)
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none', // Firefox
                        msOverflowStyle: 'none', // IE and Edge
                    }}
                >
                    <style>{`
                        /* Hide scrollbar for Chrome, Safari and Opera */
                        .w-full.h-full.overflow-auto::-webkit-scrollbar {
                            display: none;
                        }
                    `}</style>
                    <PegaWebEmbed
                        key="app-pega-embed"
                        style={{ width: '100%', height: '100%', minHeight: '100%' }}
                        {...enhancedPegaWebEmbedProps}
                    />
                </div>
            ) : (
                <>
                    {debug && console.log('[AppView] Rendering iframe with src:', currentIframeUrl)}
                    <iframe
                        key={iframeKey}
                        src={currentIframeUrl}
                        title="App View"
                        className="w-full h-full"
                        style={{
                            pointerEvents: isLoading ? 'none' : 'auto',
                            backgroundColor: '#fff',
                            border: 'none',
                            margin: 0,
                            padding: 0,
                            display: 'block',
                        }}
                    />
                    <style>{`
                        /* Hide scrollbar for App View iframe in Chrome, Safari and Opera */
                        iframe[title="App View"] {
                            border: none !important;
                        }
                    `}</style>
                </>
            )}
            {isLoading && (
                <div
                    className="absolute inset-0 z-50 w-full h-full flex flex-col items-center justify-center overflow-hidden"
                    style={{
                        pointerEvents: 'auto',
                        backgroundColor: appConfig?.splashScreenBackground || '#04142C',
                        color: appConfig?.splashScreenForeground || '#FFFFFF',
                    }}
                >
                    {appConfig?.splashScreenImage && (
                        <img src={appConfig.splashScreenImage} alt="Splash Screen" style={{ margin: "20px", display: 'block' }} />
                    )}
                    {appConfig?.splashScreenText && (
                        <p className="text-xl">{appConfig.splashScreenText}</p>
                    )}
                    {!appConfig?.splashScreenImage && !appConfig?.splashScreenText && (
                        <p className="text-xl">Loading Application...</p>
                    )}
                </div>
            )}

            {/* Agent Orb Button - Fixed position */}
            {agentConfig && !isLoading && showAgentOrb && (
                <button
                    onClick={(e) => {
                        if (isAgentOpen) {
                            // If agent is open, clicking orb closes it
                            setIsAgentOpen(false);
                        } else {
                            // Open the agent
                            setIsAgentOpen(true);
                        }
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    className="absolute z-[80]"
                    style={{
                        // When agent is open, position in top-right corner
                        // When closed, position in bottom-right corner
                        ...(isAgentOpen ? {
                            top: '20px',
                            right: '10px',
                        } : {
                            bottom: '19px',
                            right: '29px',
                        }),
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        border: '3px solid rgba(255, 255, 255, 0.3)',
                        background: agentConfig.color || '#000000',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        userSelect: 'none',
                        transform: isAgentOpen ? 'scale(0.9)' : 'scale(1)',
                    }}
                    onMouseEnter={(e) => {
                        if (!isAgentOpen) {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.4), 0 0 0 6px rgba(255, 255, 255, 0.15)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isAgentOpen) {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.1)';
                        }
                    }}
                    aria-label={isAgentOpen ? 'Close Agent' : `${agentConfig.title || 'Agent'} - Click to open`}
                    title={isAgentOpen ? 'Click to close' : 'Click to open agent'}
                >
                    {/* Chat icon with adaptive color based on background - show X when open */}
                    {isAgentOpen ? (
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                                color: isLightColor(agentConfig.color) ? '#000000' : '#ffffff',
                                pointerEvents: 'none'
                            }}
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    ) : (
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                                color: isLightColor(agentConfig.color) ? '#000000' : '#ffffff',
                                pointerEvents: 'none'
                            }}
                        >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                    )}

                    {/* Pulsing ring effect - hide when agent is open */}
                    {!isAgentOpen && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: '-8px',
                                borderRadius: '50%',
                                border: '2px solid rgba(255, 255, 255, 0.4)',
                                animation: 'orb-pulse 2s ease-in-out infinite',
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                </button>
            )}

            {/* Agent Overlay */}
            {isAgentOpen && (
                <div
                    className="absolute inset-0 z-[70]"
                    style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        flexDirection: 'column',
                        animation: 'fadeIn 0.3s ease',
                    }}
                >
                    {/* Agent View Container */}
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <AgentView />
                    </div>
                </div>
            )}

            <style>{`
                @keyframes orb-pulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 0.6;
                    }
                    50% {
                        transform: scale(1.15);
                        opacity: 0.3;
                    }
                }
                
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
}